import { api } from '../../api/client';

/**
 * Document Management ▸ Documents — the published-document reader's API surface.
 *
 * Read-only by design (BO UI Standard Class E). The base is deliberately its OWN
 * (`published-documents`), not more actions under `documents` — that route belongs to
 * Settings ▸ Document Creator, and two controllers on one base throw
 * AmbiguousMatchException per request rather than failing at startup.
 */

const BASE = '/api/v1/published-documents';

interface Envelope<T> { success: boolean; data: T }

/** One category on the rail: a live DocumentCategory that currently holds at least one
 *  readable document. A category with nothing readable is simply absent. */
export interface PublishedCategory {
  id: string;
  name: string;
  documentCount: number;
}

/** One readable document — the legacy `Manual_ListView_Category` column set. Every row
 *  returned is openable (the server rule already requires an active file), so there is no
 *  "available?" flag to carry. */
export interface PublishedDocument {
  oid: string;
  name: string | null;
  subName: string | null;
  description: string | null;
  versionNumber: string | null;
  revNumber: string | null;
}

export const documentsApi = {
  categories: () =>
    api.get<Envelope<PublishedCategory[]>>(`${BASE}/categories`).then(r => r.data.data),

  byCategory: (categoryId: string) =>
    api.get<Envelope<PublishedDocument[]>>(`${BASE}/by-category/${encodeURIComponent(categoryId)}`)
       .then(r => r.data.data),

  /**
   * The document's current PDF as an object URL, for the View modal.
   *
   * Fetched as an authenticated blob: a bare `<iframe src>` on the endpoint cannot carry
   * the JWT it requires. The caller OWNS the returned URL and must `URL.revokeObjectURL`
   * it when the modal closes, or the blob leaks for the life of the tab.
   */
  fileUrl: async (documentOid: string): Promise<string> => {
    const res = await api.get<Blob>(`${BASE}/${encodeURIComponent(documentOid)}/file`, { responseType: 'blob' });
    const blob = res.data instanceof Blob
      ? res.data
      : new Blob([res.data as BlobPart], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  },
};
